import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Banknote,
  ClipboardList,
  FileText,
  KeyRound,
  LogOut,
  Settings,
  Store,
  UtensilsCrossed,
} from "lucide-react";
import { api, ApiError, type PollDto, type PortalPayload } from "../lib/api";
import { Spinner, Notice } from "../components/ui";
import EventPanel from "../components/admin/EventPanel";
import PollsPanel from "../components/admin/PollsPanel";
import StudentsPanel from "../components/admin/StudentsPanel";
import MenuPanel from "../components/admin/MenuPanel";
import BudgetPanel from "../components/admin/BudgetPanel";
import QuotesPanel from "../components/admin/QuotesPanel";
import ContentPanel from "../components/admin/ContentPanel";

const TABS = [
  { key: "event", label: "Event", icon: Settings },
  { key: "polls", label: "Polls", icon: ClipboardList },
  { key: "students", label: "Students & votes", icon: KeyRound },
  { key: "menu", label: "Menu", icon: UtensilsCrossed },
  { key: "budget", label: "Budget & income", icon: Banknote },
  { key: "quotes", label: "Vendor quotes", icon: Store },
  { key: "content", label: "Content & exports", icon: FileText },
] as const;

type TabKey = (typeof TABS)[number]["key"];

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [username, setUsername] = useState("");
  const [tab, setTab] = useState<TabKey>("event");
  const [portal, setPortal] = useState<PortalPayload | null>(null);
  const [polls, setPolls] = useState<PollDto[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [portalData, pollData, adminItems, adminSummary] = await Promise.all([
        api.portal(),
        api.adminGet<{ polls: PollDto[] }>("/polls"),
        api.adminGet<{ items: PortalPayload["budgetItems"] }>("/budget/items"),
        api.adminGet<{ summary: PortalPayload["budgetSummary"] }>("/budget/summary"),
      ]);
      // The committee sees unredacted budget data (vendor rates included).
      setPortal({
        ...portalData,
        budgetItems: adminItems.items,
        budgetSummary: adminSummary.summary,
      });
      setPolls(pollData.polls);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setAuthed(false);
        return;
      }
      setError("Could not load admin data.");
    }
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        const me = await api.me();
        setUsername(me.username);
        setAuthed(true);
        await load();
      } catch {
        setAuthed(false);
      }
    })();
  }, [load]);

  const logout = async () => {
    try {
      await api.logout();
    } finally {
      navigate("/admin/login");
    }
  };

  if (authed === null) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner label="Checking session" />
      </div>
    );
  }

  if (authed === false) {
    return (
      <div className="mx-auto flex min-h-screen max-w-lg flex-col items-center justify-center gap-4 px-5">
        <Notice tone="error">Administrator login required.</Notice>
        <Link to="/admin/login" className="rounded-full bg-[#AFDDFF] px-6 py-2.5 text-sm font-medium text-black">
          Go to login
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <header className="sticky top-0 z-30 border-b border-white/10 bg-black/80 backdrop-blur">
        <div className="mx-auto flex max-w-shell flex-wrap items-center justify-between gap-4 px-5 py-4 md:px-10">
          <div>
            <p className="font-graphik text-2xl text-white">BBA Freshers 2026</p>
            <p className="text-xs text-wandor-muted">Committee dashboard · signed in as {username}</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-wandor-muted transition-colors hover:text-wandor-prompt">
              View public site
            </Link>
            <button
              type="button"
              onClick={() => void logout()}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-wandor-text transition-colors hover:bg-white/10"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" /> Sign out
            </button>
          </div>
        </div>
        <div className="mx-auto max-w-shell overflow-x-auto px-5 pb-3 md:px-10">
          <div className="flex gap-2" role="tablist" aria-label="Admin sections">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.key}
                  onClick={() => setTab(t.key)}
                  className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    tab === t.key
                      ? "bg-[#AFDDFF] text-black"
                      : "bg-white/[0.04] text-wandor-text hover:bg-white/10"
                  }`}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-shell px-5 py-8 md:px-10">
        {error && (
          <div className="mb-6">
            <Notice tone="error">{error}</Notice>
          </div>
        )}
        {!portal ? (
          <Spinner label="Loading dashboard" />
        ) : (
          <div className="space-y-6">
            {tab === "event" && <EventPanel event={portal.event} onSaved={() => void load()} />}
            {tab === "polls" && <PollsPanel polls={polls} onSaved={() => void load()} />}
            {tab === "students" && <StudentsPanel />}
            {tab === "menu" && (
              <MenuPanel
                categories={portal.menu.categories}
                packages={portal.menu.packages}
                items={portal.menu.items}
                onSaved={() => void load()}
              />
            )}
            {tab === "budget" && <BudgetPanel data={portal} onSaved={() => void load()} />}
            {tab === "quotes" && <QuotesPanel quotes={portal.quotes} onSaved={() => void load()} />}
            {tab === "content" && (
              <ContentPanel
                announcements={portal.announcements}
                faqs={portal.faqs}
                surplus={portal.surplus}
                event={portal.event}
                pricing={portal.pricing}
                onSaved={() => void load()}
              />
            )}
          </div>
        )}
      </main>
    </div>
  );
}
